<script lang="ts" setup>
import { watch } from 'vue';
import type { Location } from '@sembo-private/maps';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppFormButton, AppMessageBox, AppSvgIcon, AppToggle } from '@libs/ui';
import type {
  FlightAutoCompleteSuggestion,
  PortInfo,
} from '@libs/trip-planner-interfaces';

import { AirportSelector } from '../airportSelector';

import type {
  AddFlightsModalInitialRoute,
  AddFligthsModalRoute,
} from './addFlightsModal.model';
import { useAddFlighstModalStore } from './store/addFlightsModalStore';

interface AddFlightsViewProps {
  routes: AddFlightsModalInitialRoute[];
  startPositon: Location | undefined;
  endPosition: Location | undefined;
  isEditing?: boolean;
}

const props = defineProps<AddFlightsViewProps>();

interface AddFlightsViewEmits {
  (event: 'routes', routes: AddFligthsModalRoute[]): void;
  (event: 'is-editing', isEditing: boolean): void;
}

const emit = defineEmits<AddFlightsViewEmits>();

const { t } = i18n.global;

const addFlightsModalStore = useAddFlighstModalStore();

watch(
  props.routes,
  () => {
    addFlightsModalStore.initializeRoutes(
      props.routes,
      props.startPositon?.position,
      props.endPosition?.position
    );
    emit('routes', addFlightsModalStore.routes);
  },
  { immediate: true }
);

watch(
  () => props.isEditing,
  (newValue) => {
    if (!newValue) {
      addFlightsModalStore.clearEditingAirport();
    }
  }
);

function updateAirport(airportSuggestion: FlightAutoCompleteSuggestion): void {
  addFlightsModalStore.updateAirport(airportSuggestion);
  emit('is-editing', false);
}

function openAirportSelection(
  airport: PortInfo,
  index: number,
  isEditingFromAirport: boolean
): void {
  addFlightsModalStore.setEditingAirport(airport, index, isEditingFromAirport);
  emit('is-editing', true);
}

function toggleRoute(_event: boolean, index: number): void {
  const route = addFlightsModalStore.routes[index];
  if (route) {
    addFlightsModalStore.toggleRoute(route);
  }
}
</script>

<template>
  <template v-if="addFlightsModalStore.isEditing">
    <AirportSelector
      :initial-airport="addFlightsModalStore.editingAirport?.label"
      @airport-selected="updateAirport($event)"
    />
  </template>
  <template v-else>
    <div class="add-flights-route-list">
      <div
        v-for="(route, index) in addFlightsModalStore.routes"
        :key="index"
        class="add-flights-route"
      >
        <div class="route-header">
          <div class="route-description">
            {{ `${route.from} - ${route.to}` }}
          </div>
          <AppToggle
            :checked="route.isEnabled"
            @toggle="toggleRoute($event, index)"
          />
        </div>

        <AppMessageBox
          v-if="
            route.fromAirport.code === route.toAirport.code &&
            !!route.fromAirport.code &&
            !!route.toAirport.code &&
            route.isEnabled
          "
          :text="$t('modals.addFlights.different_airports_validation')"
          message-type="tertiary"
          class="message-box"
        >
          <template #left-icon>
            <AppSvgIcon name="infoCircle" color="color-secondary-40" />
          </template>
        </AppMessageBox>

        <div
          class="route-body"
          :class="{
            'no-fligts': !route.isEnabled,
          }"
        >
          <template v-if="route.isEnabled">
            <AppFormButton
              class="airport-selection-button"
              size="xxl"
              :wide="true"
              :icon="{ name: 'airplaneTakeoff' }"
              text-align="align-left"
              @click="openAirportSelection(route.fromAirport, index, true)"
            >
              <template v-if="route.fromAirport.label">
                {{ route.fromAirport.label }}
              </template>
              <template v-else>
                {{ $t('modals.addFlights.choose_departing_airport') }}
              </template>
            </AppFormButton>
            <AppFormButton
              class="airport-selection-button"
              size="xxl"
              :wide="true"
              :icon="{ name: 'airplaneLanding' }"
              text-align="align-left"
              @click="openAirportSelection(route.toAirport, index, false)"
            >
              <template v-if="route.toAirport.label">
                {{ route.toAirport.label }}
              </template>
              <template v-else>
                {{ $t('modals.addFlights.choose_arriving_airport') }}
              </template>
            </AppFormButton>
          </template>
          <div v-else class="no-flights-info">
            <AppSvgIcon name="infoCircle" />
            <span>
              {{ t('modals.addFlights.no_flights_added') }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<style lang="scss" scoped>
.add-flights-route-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-32);
  margin-bottom: var(--spacing-24);

  .add-flights-route {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-12);

    .route-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      .route-description {
        @include font-body-m-light;
      }
    }

    .message-box {
      justify-content: flex-start;
    }

    .route-body {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-12);
      position: relative;

      &:not(.no-fligts) {
        &:before {
          position: absolute;
          z-index: 1;
          content: '';
          height: 90%;
          width: 1px;
          margin-top: var(--spacing-8);
          margin-left: calc(var(--spacing-24) + var(--spacing-12));
          border-left: 1px dotted var(--color-secondary-70);
        }

        .airport-selection-button {
          position: relative;
          z-index: 2;
        }
      }

      .no-flights-info {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: var(--spacing-12);
        @include font-body-s-light;
        color: var(--color-secondary-60);
      }
    }
  }
}
</style>
